/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  USER_DETAILS_QUERY_KEY,
  SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY,
  USER_REGION_SETTINGS_QUERY_KEY,
  USER_CARD_INFO,
} from '../sharedQueries';
import {
  USA_CARD_GET_STATUS,
  USA_CARD_GET_BALANCE,
  MONAVATE_CARD_STATUS,
  MONAVATE_CARD_BALANCE,
  CONNECT_FINANCIAL_CARD_STATUS,
  CONNECT_FINANCIAL_CARD_BALANCE,
} from '../../screens/Card/queries';
import { cardProviders, MONAVATE_MAX_EMBOSSED_NAME_LENGTH } from '../../screens/Card/constants';
import { useSetting } from './settings';

export const resolveUserCardProvider = (user, userCardInfo) => {
  if (userCardInfo?.hasCard) {
    return userCardInfo?.details?.provider;
  }
  if (user?.card_provider === cardProviders.CONNECT_FINANCIAL) {
    return cardProviders.CONNECT_FINANCIAL;
  }
  return user?.selected_country?.default_card_provider || null;
};

export const useUserCardProvider = (enabled = true) => {
  const {
    data,
    error,
    isLoading,
    refetch: refetchUserDetails,
  } = useQuery(USER_DETAILS_QUERY_KEY, { enabled: false });

  const {
    data: userCardInfo,
    error: userCardInfoError,
    isLoading: isLoadingUserCardInfo,
    refetch: refetchUserCardInfo,
  } = useQuery(USER_CARD_INFO, { enabled: false });

  const retechCardProviderInfo = () => {
    refetchUserDetails();
    refetchUserCardInfo();
  };

  useEffect(() => {
    if (enabled) {
      retechCardProviderInfo();
    }
  }, []);

  const result = useMemo(() => {
    const res = {
      cardProvider: null,
      error: null,
      isLoading: isLoading || isLoadingUserCardInfo,
    };

    if (error || userCardInfoError) {
      res.error = error || userCardInfoError;
    }

    if (data && userCardInfo && !isLoading && !isLoadingUserCardInfo) {
      res.cardProvider = resolveUserCardProvider(data.user, userCardInfo);
    }

    return res;
  }, [data, error, isLoading, userCardInfoError, isLoadingUserCardInfo, userCardInfo]);

  return { ...result, refetch: retechCardProviderInfo };
};

export const useUserDefaultCardCurrency = () => {
  const result = {
    defaultCardCurrency: null,
    error: null,
  };

  const { data, error, isLoading } = useQuery(USER_DETAILS_QUERY_KEY);

  if (data && !isLoading) {
    const { user } = data;
    if (error) {
      result.error = error;
    }
    if (user && user.selected_country && user.selected_country.default_card_currency) {
      result.defaultCardCurrency = user.selected_country.default_card_currency;
    }
  }

  return result;
};

export const useUserPersonalInfo = () => {
  const result = {
    firstName: null,
    lastName: null,
    countryCode: null,
    phoneNumber: null,
    countryId: null,
    id: null,
    authyId: null,
    fa2Activated: false,
    totpEnabled: false,
    totpActive: false,
    email: null,
    riskTier: null,
    defaultCardCurrency: null,
    countryDisplayName: null,
    allowOxygenYield: false,
    nextTier: null,
    zendeskJWT: null,
    selectedCountry: null,
    cardProvider: null,
    isUS: false,
    dateOfBirth: null,
    houseNumber: '',
    street: '',
    city: '',
    usState: '',
    postCode: '',
    normalizedPhoneNumber: null,
    currency: '',
    verificationState: '',
  };

  const { data, error, isLoading } = useQuery(USER_DETAILS_QUERY_KEY);

  if (data && !isLoading) {
    const { user } = data;

    if (error) {
      result.error = error;
    }
    if (user) {
      if (user.first_name) {
        result.firstName = user.first_name;
      }
      if (user.last_name) {
        result.lastName = user.last_name;
      }
      if (user.country_code) {
        result.countryCode = user.country_code;
      }
      if (user.country_id) {
        result.countryId = user.country_id;
      }
      if (user.phone_number) {
        result.phoneNumber = user.phone_number;
      }
      if (user.id) {
        result.id = user.id;
      }
      if (user.authy_id) {
        result.authyId = user.authy_id;
      }
      if (user.totp_enabled) {
        result.totpEnabled = user.totp_enabled;
      }
      if (user.totp_active) {
        result.totpActive = user.totp_active;
      }
      if (user.fa2_activated) {
        result.fa2Activated = user.fa2_activated;
      }
      if (user.email) {
        result.email = user.email;
      }
      if (user.riskTier) {
        result.riskTier = user.riskTier;
      }
      if (user.nextTier) {
        result.nextTier = user.nextTier;
      }
      if (user.allow_oxygen_yield) {
        result.allowOxygenYield = user.allow_oxygen_yield;
      }
      if (user.selected_country) {
        result.selectedCountry = user.selected_country;
      }
      if (user.selected_country && user.selected_country.default_card_currency) {
        result.defaultCardCurrency = user.selected_country.default_card_currency;
      }
      if (user.selected_country && user.selected_country.displayName) {
        result.countryDisplayName = user.selected_country.displayName;
      }
      if (user.zendeskJWT) {
        result.zendeskJWT = user.zendeskJWT;
      }
      if (user.card_provider) {
        result.cardProvider = user.card_provider;
      }
      if (user?.selected_country && user?.selected_country?.iso3166alpha2) {
        result.isUS = user?.selected_country?.iso3166alpha2 === 'US';
      }
      if (user?.date_of_birth) {
        result.dateOfBirth = user?.date_of_birth;
      }
      if (user?.house_number) {
        result.houseNumber = user?.house_number;
      }
      if (user?.street) {
        result.street = user?.street;
      }
      if (user?.us_state) {
        result.usState = user?.us_state;
      }
      if (user?.city) {
        result.city = user?.city;
      }
      if (user?.post_code) {
        result.postCode = user?.post_code;
      }
      if (user?.normalized_phone_number) {
        result.normalizedPhoneNumber = user?.normalized_phone_number;
      }
      if (user?.currency) {
        result.currency = user?.currency;
      }
      result.verificationState = user?.verification_state;
    }
  }

  return { ...result, isLoading };
};

export const useUserCountry = () => {
  const {
    data: supportedCountries,
    error: supportedCountriesError,
    isLoading: supportedCountriesLoading,
  } = useQuery(SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY);

  const {
    data: userData,
    error: userError,
    isLoading: userLoading,
  } = useQuery(USER_DETAILS_QUERY_KEY);

  let result;

  if (userData && supportedCountries && !supportedCountriesLoading && !userLoading) {
    // eslint-disable-next-line prefer-destructuring
    result = supportedCountries.filter((country) => country.id === userData?.user?.country_id)[0];
    if (supportedCountriesError || userError) {
      result.error = supportedCountriesError || userError;
    }
  }

  return result;
};

export const cardTypesMappings = {
  [cardProviders.GALILEO]: USA_CARD_GET_STATUS,
  [cardProviders.MONAVATE]: MONAVATE_CARD_STATUS,
  [cardProviders.CONNECT_FINANCIAL]: CONNECT_FINANCIAL_CARD_STATUS,
};

export const useCardStatus = (options = {}) => {
  const {
    cardProvider,
    isLoading: isLoadingCardProvider,
    refetch: refetchCardProvider,
  } = useUserCardProvider(options?.enabled);

  // the loading from useQuery not working always true that's why this one added
  const [isLoadingCardStatus, setIsLoadingCardStatus] = useState(false);

  const {
    data,
    error,
    refetch: refetchStatus,
  } = useQuery(cardTypesMappings[cardProvider], {
    ...options,
    enabled: !!cardProvider,
    onSettled: (response, errorResponse) => {
      if (options?.onSettled) {
        options?.onSettled(response, errorResponse);
      }
      setIsLoadingCardStatus(false);
    },
  });

  const refetchCardStatus = () => {
    refetchCardProvider();
  };

  useEffect(() => {
    if (cardProvider) {
      setIsLoadingCardStatus(true);
      refetchStatus();
    }
  }, [cardProvider]);

  const result = useMemo(() => {
    const res = {
      data: null,
      error: null,
    };

    if (error) {
      res.error = error;
    } else {
      res.data = data;
    }

    return res;
  }, [data, error]);

  return {
    ...result,
    isLoading: isLoadingCardProvider || isLoadingCardStatus,
    refetch: refetchCardStatus,
  };
};

export const cardTypesBalanceMappings = {
  [cardProviders.GALILEO]: USA_CARD_GET_BALANCE,
  [cardProviders.MONAVATE]: MONAVATE_CARD_BALANCE,
  [cardProviders.CONNECT_FINANCIAL]: CONNECT_FINANCIAL_CARD_BALANCE,
};

export const useCardBalances = (options = {}) => {
  const { cardProvider, isLoading: isLoadingCardProvider } = useUserCardProvider();

  const { data, isLoading, error, refetch } = useQuery(cardTypesBalanceMappings[cardProvider], {
    ...options,
    enabled: false,
  });

  useEffect(() => {
    if (cardProvider && !isLoadingCardProvider) {
      refetch();
    }
  }, [cardProvider, isLoadingCardProvider]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useUserAllowStakingFeature = () => {
  const [allowStakingFeature] = useSetting('app_features', 'feature_allow_staking');

  const { data: userRegionSettings } = useQuery(USER_REGION_SETTINGS_QUERY_KEY);

  return allowStakingFeature && userRegionSettings?.allow_staking;
};

export function formatMonovateCardHolderName(firstName, lastName) {
  if (firstName.length + lastName.length < MONAVATE_MAX_EMBOSSED_NAME_LENGTH) {
    return `${firstName} ${lastName}`;
  }

  const firstWordOfFirstName = firstName.split(' ')[0];
  const lastWordOfLastName = lastName.split(' ').pop();

  if (firstWordOfFirstName.length + lastWordOfLastName.length < MONAVATE_MAX_EMBOSSED_NAME_LENGTH) {
    return `${firstWordOfFirstName} ${lastWordOfLastName}`;
  }

  return `${firstName[0]}. ${lastName.split(' ').pop().slice(0, 18)}`;
}
